import { SETUP_HINT, type SetupNeed } from "@shared/connectors/setupNeed";
import { SkyBadge, SkyText, type SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import type {
  ConnectionRunOutcome,
  DataflowLastRun,
  DataflowSummary,
  DataflowWaiting,
} from "@shared/types/dataflows";
import type { RunStatus } from "@shared/types/runs";
import { runStatusPresentation } from "@/components/runs/runFormatting";
import { instancePath } from "@/lib/instancePath";
import { absolutePath } from "@/lib/routes";
import type { RunActivityPhase } from "@/lib/runs/runActivity";

/**
 * A connection's one user-facing status (owner feedback, 2026-09-28).
 *
 * The owner: "The state says Ready when the import is actually running", and "as an end user
 * Ready doesn't really mean anything ... Think like an enterprise product like Fivetran." The API
 * keeps `state` (draft, ready, disabled: whether the connection is set up), and the runner keeps
 * the runs; a reader wants one word that merges the two. It is derived here, in the client, from
 * what the list and the editor already read, so the server and the runner change nothing:
 *
 * - **Disabled**: turned off by hand (`state` disabled).
 * - **Running**: an import is queued or running (the live phase, the list's `lastRun`,
 *   `activeRunId` or the runner's `is_processing` lock). It wins over the setup state: a run in
 *   flight is the most current fact about a connection, even one edited back to a draft.
 * - **Setup incomplete**: `state` draft; it cannot import until it validates with a table, or
 *   while a credential is missing (#1267, the `CREDENTIAL_MISSING` block).
 * - **Waiting** (#787): its own part of the last run failed ONLY because a readiness gate refused
 *   it (the tenant's or the destination's setup is not finished; `lastRun.waiting`, from the
 *   server). Nothing is wrong with the connection or the source, so it is not Failed; nothing was
 *   loaded either, so it is never Active. The reason and the next step go beside it.
 * - **Failed**: this connection's own part of its last SETTLED run failed, even when that run
 *   also imported other connections and so reads Partly failed. A last run cancelled with no
 *   failed step of its own settled nothing, so the server answers the newest settled run's
 *   outcome instead (`lastRun.outcomeRunId`, #460 option 2): a Run now and Cancel never hides an
 *   unrepaired failure.
 * - **Active**: its own part of the last run succeeded, or partly failed with tables skipped (the Last
 *   import column says which), or was cancelled after an earlier good refresh.
 * - **Ready**: set up and never imported (or only cancelled before any good refresh).
 *
 * "Its own part" is `lastRun.outcome` (post-release 607e63a): the API reads it from this
 * connection's own steps in that run. `isFailed` says the same thing but carries no run id, so
 * it never outranks the newest run: with no outcome (an older server) it decides only where the
 * run's status cannot (partially succeeded or cancelled), and a SUCCEEDED run beats it, because
 * finalize never sets it on a run with no failed step, so it can only be older than that run.
 *
 * A finished live phase (the tab saw the run end) beats a list read that has not caught up yet;
 * a phase that knows nothing (loading, unavailable, never) leaves the summary in charge.
 */

export const CONNECTION_STATUSES = [
  "setup_incomplete",
  "ready",
  "running",
  "waiting",
  "failed",
  "active",
  "disabled",
] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export type ConnectionStatusInput = Pick<
  DataflowSummary,
  "state" | "isProcessing" | "isFailed" | "lastRunId" | "lastRefreshAt" | "activeRunId" | "lastRun"
>;

type Outcome = "succeeded" | "failed" | "cancelled";

const PHASE_OUTCOME: Partial<Record<RunActivityPhase, Outcome>> = {
  succeeded: "succeeded",
  partial: "succeeded",
  failed: "failed",
  cancelled: "cancelled",
};

function outcomeOfStatus(status: DataflowLastRun["outcome"]): Outcome | null {
  switch (status) {
    case "succeeded":
    case "partially_succeeded":
      return "succeeded";
    case "failed":
      return "failed";
    case "cancelled":
      return "cancelled";
    default:
      return null;
  }
}

/**
 * This connection's own outcome in the listed run, when the API sent it and, for a live phase,
 * only when the phase is that same run's (a newer run cannot borrow an older run's answer).
 */
function ownOutcome(dataflow: ConnectionStatusInput, runId?: string | null): Outcome | null {
  const run = dataflow.lastRun;
  if (!run?.outcome) return null;
  if (runId !== undefined && runId !== run.runId) return null;
  return outcomeOfStatus(run.outcome);
}

/**
 * What a live phase says of this connection. A phase is the WHOLE run's, so once that run is
 * listed with this connection's own outcome, the own outcome decides, for every finished phase
 * (#1014): a force reset that lands the run Failed because another connection's step failed
 * (`forceResetRunStatus`) leaves this connection's own steps Cancelled, and the connection's
 * page then reads what its list row reads. Without that outcome, a `partial` reads the flag and
 * any other finished phase its own outcome; no phase (or a phase still moving) says nothing.
 */
function phaseOutcomeOf(
  dataflow: ConnectionStatusInput,
  phase: RunActivityPhase | null,
  phaseRunId: string | null,
): Outcome | null {
  if (!phase) return null;
  const finished = PHASE_OUTCOME[phase];
  if (!finished) return null;
  const own = ownOutcome(dataflow, phaseRunId);
  if (own) return own;
  if (phase === "partial") return dataflow.isFailed ? "failed" : "succeeded";
  return finished;
}

/**
 * `lastRun.status` is the WHOLE run's status, decided by connection (#443): partially_succeeded
 * when some connections landed their data and others did not, when one landed only some of its
 * tables, or when dbt failed after every one landed. A connection that extracted and then landed
 * nothing makes a one-connection run failed. `lastRun.outcome` is this connection's own, so it
 * is read first. Without it (an older
 * server) the flag decides only what the run's status cannot: a partial or cancelled run.
 */
function listedOutcome(dataflow: ConnectionStatusInput): Outcome | null {
  const own = ownOutcome(dataflow);
  if (own) return own;
  switch (dataflow.lastRun?.status) {
    case "succeeded":
      // A flag beside a run that succeeded is older than it: finalize sets it only from a
      // failed step, and a run with a failed step never reads succeeded.
      return "succeeded";
    case "partially_succeeded":
      return dataflow.isFailed ? "failed" : "succeeded";
    case "failed":
      return "failed";
    case "cancelled":
      // #460 (option 2): a cancel settles nothing, so the flag, which describes the newest
      // settled run, decides.
      return dataflow.isFailed ? "failed" : "cancelled";
    default:
      break;
  }
  if (dataflow.isFailed) return "failed";
  if (dataflow.lastRefreshAt || dataflow.lastRunId) return "succeeded";
  return null;
}

/**
 * #2094: the run rows decide, never the connection's lock flag beside them. `activeRunId` and
 * `lastRun.status` are read from the run rows themselves. Finalize (`runner/steps/finalize.ts`)
 * writes the run row, then the connection's summary (`is_processing`), then the terminal frame,
 * so the frame's own refresh already reads both; but a read that lands between the first two
 * writes (the 30 s poll, a throttled step-frame refresh, the Overview's own read) paired a
 * finished run with a connection still "processing", and the row read Running beside Succeeded
 * until the next read. The flag decides only for an answer that carries no `activeRunId`: an
 * older answer, or one whose run facts could not be read (`connectorUsage.ts` then leaves it out).
 * The Status cell, the Overview's counts, the row menu's start gating and Cancel run
 * (`pages/connections/list.tsx`) and the Last import fallback all read this one rule.
 */
export function isListedRunning(
  dataflow: Pick<ConnectionStatusInput, "isProcessing" | "activeRunId" | "lastRun">,
): boolean {
  const status = dataflow.lastRun?.status;
  if (status === "queued" || status === "running") return true;
  if (dataflow.activeRunId !== undefined) return dataflow.activeRunId !== null;
  return dataflow.isProcessing;
}

/**
 * `phaseRunId` names the run `phase` describes, so a live `partial` can read this connection's
 * own outcome when the list already has that run; otherwise the flag stands in for it.
 */
export function connectionStatusOf(
  dataflow: ConnectionStatusInput,
  phase: RunActivityPhase | null = null,
  phaseRunId: string | null = null,
): ConnectionStatus {
  if (dataflow.state === "disabled") return "disabled";
  if (phase === "queued" || phase === "running") return "running";
  // A live `partial` is the whole run's phase; this connection's own part decides.
  const phaseOutcome = phaseOutcomeOf(dataflow, phase, phaseRunId);
  if (phaseOutcome === null && isListedRunning(dataflow)) return "running";
  if (dataflow.state === "draft") return "setup_incomplete";
  const outcome = phaseOutcome ?? listedOutcome(dataflow);
  if (outcome === "failed") {
    // A live phase of a newer run cannot borrow the listed run's reason.
    const sameRun = phaseOutcome === null || phaseRunId === dataflow.lastRun?.runId;
    return sameRun && connectionWaitingOf(dataflow) ? "waiting" : "failed";
  }
  if (outcome === "succeeded") return "active";
  if (outcome === "cancelled") return dataflow.lastRefreshAt ? "active" : "ready";
  return "ready";
}

/**
 * #787: why the connection waits, when its last run's failure was only a readiness gate's
 * refusal; null otherwise. The server's words (`lastRun.waiting`), never composed here.
 */
export function connectionWaitingOf(
  dataflow: Pick<DataflowSummary, "lastRun">,
): DataflowWaiting | null {
  const run = dataflow.lastRun;
  return run?.outcome === "failed" && run.waiting ? run.waiting : null;
}

type ListedRun = Pick<DataflowLastRun, "runId" | "status" | "outcome" | "outcomeRunId" | "waiting">;

/**
 * #460 (option 2): the listed run's OWN word, for a cell that also shows that run's time and
 * links to it. When the server answers an earlier settled run's outcome (`outcomeRunId`), the
 * listed run itself was cancelled with no failed step, so it reads Cancelled, and the earlier
 * run is the separate line `SettledImportLine` names.
 */
export function listedRunOwnStatus(
  run: Pick<ListedRun, "status" | "outcome" | "outcomeRunId">,
): RunStatus {
  if (run.outcomeRunId) return "cancelled";
  return run.outcome ?? run.status;
}

/** #460 (option 2): the earlier settled run the list's outcome describes, when it is not the listed run. */
export function settledImportOf(
  run: Pick<ListedRun, "outcome" | "outcomeRunId" | "waiting"> | null | undefined,
): { runId: string; outcome: ConnectionRunOutcome; waiting: boolean } | null {
  if (!run?.outcomeRunId || !run.outcome) return null;
  return {
    runId: run.outcomeRunId,
    outcome: run.outcome,
    waiting: run.outcome === "failed" && Boolean(run.waiting),
  };
}

/**
 * #460 (option 2): "Last settled import: Failed", linked to that run, under a cancelled last run
 * whose outcome the Status column reads from an earlier run. Nothing when the listed run is its
 * own outcome. `instanceId` links into that instance (`/i/<id>/runs/<run>`), for a screen outside
 * it (the platform's Used by table); without it the link stays in the current instance. `compact`
 * reads "Settled: Failed" on one line, with the full words as its hover and accessible name, for
 * a narrow cell.
 */
export function SettledImportLine({
  run,
  testId,
  instanceId,
  compact = false,
}: {
  run: Pick<ListedRun, "outcome" | "outcomeRunId" | "waiting"> | null | undefined;
  testId?: string;
  instanceId?: string;
  compact?: boolean;
}) {
  const settled = settledImportOf(run);
  if (!settled) return null;
  const label = settled.waiting ? "Waiting" : runStatusPresentation(settled.outcome).label;
  const inner = `/runs/${settled.runId}`;
  const full = `Last settled import: ${label}`;
  return (
    <Link
      href={absolutePath(instanceId ? instancePath(instanceId, inner) : inner)}
      className={
        compact
          ? "whitespace-nowrap underline-offset-2 hover:underline"
          : "underline-offset-2 hover:underline"
      }
      aria-label={`${full}, open the run`}
      title={compact ? full : undefined}
      data-testid={testId}
    >
      <SkyText size={200} color="accent">
        {compact ? `Settled: ${label}` : full}
      </SkyText>
    </Link>
  );
}

/** "This tenant's warehouse loading is not set up yet. Ask Skypoint support to ..." */
export function waitingLine(waiting: DataflowWaiting): string {
  return `${waiting.sentence}. ${waiting.fix}`;
}

interface StatusPresentation {
  label: string;
  /** Status tokens only (neutral, info, success, danger); never the amber warning. */
  color: Extract<SkyBadgeColor, "subtle" | "informative" | "success" | "danger">;
  appearance: "tint" | "outline";
  /** Read after the word by a screen reader, so the status is never a colour alone. */
  hint: string;
}

const PRESENTATION: Record<ConnectionStatus, StatusPresentation> = {
  setup_incomplete: {
    label: "Setup incomplete",
    color: "subtle",
    // Hollow: not finished yet, and never read as a failure.
    appearance: "outline",
    hint: SETUP_HINT.table,
  },
  ready: {
    label: "Ready",
    color: "subtle",
    appearance: "tint",
    hint: "Set up and not imported yet. It can be imported now or on a schedule.",
  },
  running: {
    label: "Running",
    color: "informative",
    appearance: "tint",
    hint: "An import is queued or running.",
  },
  waiting: {
    label: "Waiting",
    // Hollow and informative: not a failure of the connection, and never the green of a success.
    color: "informative",
    appearance: "outline",
    hint: "The last import was held back because setup is not finished, and nothing was loaded. The reason and the next step are shown with it.",
  },
  failed: {
    label: "Failed",
    color: "danger",
    appearance: "tint",
    hint: "The last import failed. Open it to see why.",
  },
  active: {
    label: "Active",
    color: "success",
    appearance: "tint",
    hint: "Set up, and its last import succeeded.",
  },
  disabled: {
    label: "Disabled",
    color: "subtle",
    appearance: "tint",
    hint: "Turned off by hand. It will not run, even on a schedule.",
  },
};

export function connectionStatusPresentation(
  status: ConnectionStatus,
  need: SetupNeed = "table",
): StatusPresentation {
  const presentation = PRESENTATION[status];
  return status === "setup_incomplete" ? { ...presentation, hint: SETUP_HINT[need] } : presentation;
}

export function ConnectionStatusBadge({
  status,
  size = "medium",
  testId,
  need = "table",
}: {
  status: ConnectionStatus;
  size?: "medium" | "large";
  testId?: string;
  need?: SetupNeed;
}) {
  const presentation = connectionStatusPresentation(status, need);
  return (
    <SkyBadge
      appearance={presentation.appearance}
      color={presentation.color}
      size={size}
      testId={testId}
      aria-label={`Status: ${presentation.label}. ${presentation.hint}`}
    >
      {presentation.label}
    </SkyBadge>
  );
}
